import { useMutation } from '@tanstack/react-query';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type DemoFormInput } from '~/components/Modals/demoFormValidation';
import {
  DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID,
  DEFAULT_PIXEL_IN_APP_DEMO_FORM_ID,
  DEMO_FORM_NOT_CONFIGURED_ERROR,
  DEMO_FORM_SUBMIT_ERROR_MESSAGE,
  isDefaultPixelSdkConfigured,
} from '~/constants/defaultPixel';
import { mapDemoFormValuesToDefaultResponses } from '~/utils/default-pixel/mapDemoFormValuesToDefaultResponses';

export type UseSubmitDemoLeadFormMutationParams = DemoFormInput;

export const useSubmitDemoLeadFormMutation = () =>
  useMutation({
    mutationFn: async (params: UseSubmitDemoLeadFormMutationParams) => {
      if (!isDefaultPixelSdkConfigured()) {
        throw new Error(DEMO_FORM_NOT_CONFIGURED_ERROR);
      }

      const pixelFormId = DEFAULT_PIXEL_IN_APP_DEMO_FORM_ID;
      const htmlFormId = DEFAULT_PIXEL_IN_APP_DEMO_FORM_HTML_ID;

      if (!pixelFormId || !htmlFormId) {
        throw new Error(DEMO_FORM_NOT_CONFIGURED_ERROR);
      }

      const sdk = window.DefaultPixelSDK;

      if (!sdk?.submitForm) {
        throw new Error('Default Pixel SDK is not loaded');
      }

      const result = await sdk.submitForm({
        pixelFormId,
        formId: htmlFormId,
        responses: mapDemoFormValuesToDefaultResponses(params),
        metadata: {
          lead_source: 'Web (demo request)',
          lead_source_detail: 'Web form - In-app',
        },
        autoDisplayScheduler: false,
      });

      if (!result.success) {
        throw new Error(DEMO_FORM_SUBMIT_ERROR_MESSAGE);
      }

      return result;
    },
    onError: (error, params) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to submit Default demo lead form',
        metadata: {
          company_size: params.company_size,
          isConfigured: isDefaultPixelSdkConfigured(),
        },
      });
    },
  });
